import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Users2, Crown, TrendingUp, Percent, CalendarDays, MessageCircle, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

const Stat = ({ Icon, label, value, sub, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="glass rounded-2xl border border-primary/15 p-5 frame-pulse"
  >
    <div className="flex items-center gap-2 text-primary/70 mb-2">
      <Icon size={16} /> <span className="text-[11px] uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-display text-3xl text-foreground/95 tabular-nums">{value}</div>
    {sub && <p className="text-xs text-foreground/50 mt-1">{sub}</p>}
  </motion.div>
);

export default function AnalyticsSection() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics")
      .then(({ data }) => setD(data))
      .catch(() => toast.error("Chargement des statistiques impossible"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center gap-3 text-foreground/50 p-8 justify-center"><Loader2 className="animate-spin" size={18} /> Chargement…</div>;
  }
  if (!d) return null;

  const maxCount = Math.max(1, ...d.series.map((s) => s.count));

  return (
    <div className="space-y-6" data-testid="analytics-section">
      <div className="glass rounded-2xl border border-primary/20 p-6 frame-pulse">
        <h3 className="font-display text-xl text-gradient-orange flex items-center gap-2">
          <BarChart3 size={19} /> Statistiques
        </h3>
        <p className="text-foreground/55 text-sm mt-1.5">Vue d'ensemble de la communauté et de la croissance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat Icon={Users2} label="Explorateurs" value={d.total} sub={`${d.admins} admin(s)`} delay={0.02} />
        <Stat Icon={Crown} label="Abonnés" value={d.subscribers} sub={`Élan ${d.plans.elan} · Cocon ${d.plans.cocon}`} delay={0.06} />
        <Stat Icon={Percent} label="Conversion" value={`${d.conversion}%`} sub="abonnés / total" delay={0.1} />
        <Stat Icon={CalendarDays} label="Inscrits 7 j" value={d.signups_7d} delay={0.14} />
        <Stat Icon={TrendingUp} label="Inscrits 30 j" value={d.signups_30d} delay={0.18} />
        <Stat Icon={MessageCircle} label="Messages 7 j" value={d.messages_7d} sub="activité des fils" delay={0.22} />
      </div>

      {/* Répartition des forfaits */}
      <div className="glass rounded-2xl border border-primary/15 p-6">
        <p className="uppercase tracking-[0.2em] text-[0.62rem] text-foreground/40 mb-4">Répartition des forfaits</p>
        <div className="space-y-3">
          {[
            { k: "free", label: "Free", n: d.plans.free, color: "bg-white/25" },
            { k: "elan", label: "Élan", n: d.plans.elan, color: "bg-sky-400/70" },
            { k: "cocon", label: "Cocon", n: d.plans.cocon, color: "bg-amber-400/80" },
          ].map((p) => (
            <div key={p.k} className="flex items-center gap-3">
              <span className="w-16 text-sm text-foreground/70">{p.label}</span>
              <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${d.total ? (p.n / d.total) * 100 : 0}%` }} transition={{ duration: 0.9 }} className={`h-full rounded-full ${p.color}`} />
              </div>
              <span className="w-10 text-right text-sm text-foreground/80 tabular-nums">{p.n}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Croissance 14 jours */}
      <div className="glass rounded-2xl border border-primary/15 p-6">
        <p className="uppercase tracking-[0.2em] text-[0.62rem] text-foreground/40 mb-5">Nouvelles inscriptions — 14 derniers jours</p>
        <div className="flex items-end gap-1.5 h-40" data-testid="analytics-chart">
          {d.series.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
              <div className="w-full flex-1 flex items-end">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(s.count / maxCount) * 100}%` }}
                  transition={{ duration: 0.7, delay: i * 0.03 }}
                  className="w-full rounded-t-md bg-gradient-to-t from-primary/40 to-primary/80 min-h-[3px] relative"
                  title={`${s.date} — ${s.count}`}
                >
                  {s.count > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-primary/80 opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">{s.count}</span>
                  )}
                </motion.div>
              </div>
              <span className="text-[9px] text-foreground/35 tabular-nums">{s.date.slice(8, 10)}/{s.date.slice(5, 7)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
